// Автоматична подредба на маса, добавена без схема („Конфигурация на маси"):
// първото свободно място ред по ред — отгоре надолу, отляво надясно — с
// отстояние AUTO_LAYOUT_GAP между масите. Ръчно подредените маси и обекти в
// схемата не се местят, само се заобикалят; изтрита маса оставя дупка, която
// следващата нова маса запълва.

import type { SchemaElement, SchemaRegion } from "../../api/restaurantSchema";
import {
  GRID_STEP,
  REGION_DIMENSIONS,
  REGION_EDGE_INSET,
  elementHalfExtents,
  findFreeSpot,
} from "./geometry";

/** Нова маса от страницата: малка квадратна, без завъртане (слага я BE). */
export const CONFIG_TABLE_DEFAULTS = {
  tableShape: "square",
  size: "small",
  rotation: 0,
} as const;

/** Отстоянието между масите (и до ръба на региона) — 40 см. */
export const AUTO_LAYOUT_GAP = 40;

/** Колко около входа остава проход — там не се слага маса. */
const ENTRANCE_CLEARANCE = 120;

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Масата, която предстои да се създаде, като псевдо-елемент (за размерите ѝ). */
export const draftTable = (regionId: number, seats: number): SchemaElement => ({
  id: -1,
  regionId,
  kind: "table",
  name: null,
  tableShape: CONFIG_TABLE_DEFAULTS.tableShape,
  seats,
  size: CONFIG_TABLE_DEFAULTS.size,
  rotation: CONFIG_TABLE_DEFAULTS.rotation,
  x: 0,
  y: 0,
});

/**
 * Колко място заема съществуващ елемент: реалният му размер (завъртян, със
 * столовете); входът — вратата плюс проход около нея.
 */
export const occupiedBox = (element: SchemaElement): Box => {
  const { halfWidth, halfHeight } = elementHalfExtents(element);
  const clearance = element.kind === "entrance" ? ENTRANCE_CLEARANCE : 0;
  return {
    left: element.x - halfWidth - clearance,
    top: element.y - halfHeight - clearance,
    right: element.x + halfWidth + clearance,
    bottom: element.y + halfHeight + clearance,
  };
};

const overlaps = (first: Box, second: Box) =>
  first.left < second.right &&
  second.left < first.right &&
  first.top < second.bottom &&
  second.top < first.bottom;

const snapUp = (value: number) => Math.ceil(value / GRID_STEP) * GRID_STEP;

/**
 * Първото място (центъра) за `draft` в региона, където масата заедно с
 * отстоянието около нея не застъпва нищо и не стига до ръба му. `null`, ако
 * регионът е пълен. Самият `draft` (по id) не се брои за препятствие.
 */
export const findLayoutSpot = (
  region: Pick<SchemaRegion, "shape">,
  elements: SchemaElement[],
  draft: SchemaElement,
  gap: number = AUTO_LAYOUT_GAP,
): { x: number; y: number } | null => {
  const regionSize = REGION_DIMENSIONS[region.shape];
  const { halfWidth, halfHeight } = elementHalfExtents(draft);
  const obstacles = elements
    .filter((element) => element.id !== draft.id)
    .map(occupiedBox);
  const minX = snapUp(REGION_EDGE_INSET + gap + halfWidth);
  const minY = snapUp(REGION_EDGE_INSET + gap + halfHeight);
  const maxX = regionSize.width - REGION_EDGE_INSET - gap - halfWidth;
  const maxY = regionSize.height - REGION_EDGE_INSET - gap - halfHeight;

  for (let y = minY; y <= maxY; y += GRID_STEP) {
    let x = minX;
    while (x <= maxX) {
      const candidate = {
        left: x - halfWidth - gap,
        top: y - halfHeight - gap,
        right: x + halfWidth + gap,
        bottom: y + halfHeight + gap,
      };
      const blocker = obstacles.find((box) => overlaps(candidate, box));
      if (!blocker) return { x, y };
      // Прескачаме директно зад препятствието — вместо по 10 единици.
      x = Math.max(x + GRID_STEP, snapUp(blocker.right + gap + halfWidth));
    }
  }
  return null;
};

/**
 * Къде да отиде маса в региона: мястото от подредбата, а ако регионът е пълен —
 * свободно петно от редактора на схемата (`findFreeSpot`, вътре в региона).
 * `fitted: false` значи „не се събра" — масата пак се записва, но трябва да се
 * подреди ръчно в схемата.
 */
export const placeTableInRegion = (
  region: Pick<SchemaRegion, "shape" | "elements">,
  draft: SchemaElement,
): { x: number; y: number; fitted: boolean } => {
  const spot = findLayoutSpot(region, region.elements, draft);
  if (spot) return { ...spot, fitted: true };
  const others = region.elements.filter((element) => element.id !== draft.id);
  const fallback = findFreeSpot(region.shape, others, undefined, draft);
  return { ...fallback, fitted: false };
};
